import React, { useRef } from 'react';
import { BoldIcon, ItalicIcon, ListOrderedIcon, RefreshCwIcon, UnderlineIcon } from 'lucide-react';
import type { NoteFile } from '../../../types/case';
import { bn } from '../../../utils/bn';
export interface NoteDraft {
  subject: string;
  observation: string;
  recommendation: string;
}
interface Props {
  file: NoteFile;
  draft: NoteDraft;
  onDraft: (d: NoteDraft) => void;
  bodyRef: React.RefObject<HTMLDivElement>;
  onRegenerate: () => void;
}

/** Editable formal note body (rich text) with subject, observation and recommendation. */
export function NoteEditor({
  file,
  draft,
  onDraft,
  bodyRef,
  onRegenerate
}: Props) {
  const counter = useRef(0);
  const exec = (cmd: string) => {
    bodyRef.current?.focus();
    document.execCommand(cmd);
  };
  const addPara = () => {
    const el = bodyRef.current;
    if (!el) return;
    counter.current += 1;
    const n = el.querySelectorAll('p').length + 1;
    el.insertAdjacentHTML('beforeend', `<p><b>${bn(n)}।</b> </p>`);
    el.focus();
  };
  return <section className="card p-5" aria-label="নোট সম্পাদনা" data-id="element-110" data-mp="es26yj3p7609">
      <label className="label" htmlFor="ne-sub" data-id="element-111" data-mp="arrsei1ub79s">বিষয়</label>
      <input id="ne-sub" className="input font-semibold" value={draft.subject} onChange={e => onDraft({
      ...draft,
      subject: e.target.value
    })} data-id="element-112" data-mp="as9wu1ynorss" />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2" data-id="element-113" data-mp="tu6403jjkjt">
        <span className="label mb-0" data-id="element-114" data-mp="3rzlsqpi40f1v">নোটের অনুচ্ছেদসমূহ</span>
        <div className="flex flex-wrap items-center gap-1 rounded-lg border border-line p-1" data-id="element-115" data-mp="3s16micwlntyo">
          <button type="button" className="btn btn-ghost btn-sm h-7 w-7 px-0" aria-label="বোল্ড" onMouseDown={e => {
          e.preventDefault();
          exec('bold');
        }} data-id="element-116" data-mp="3n7c89ozrk76z"><BoldIcon className="h-3.5 w-3.5" data-id="element-117" data-mp="3e87curji0tz1" /></button>
          <button type="button" className="btn btn-ghost btn-sm h-7 w-7 px-0" aria-label="ইটালিক" onMouseDown={e => {
          e.preventDefault();
          exec('italic');
        }} data-id="element-118" data-mp="3e9s4o9efhyt7"><ItalicIcon className="h-3.5 w-3.5" data-id="element-119" data-mp="34ab1pzqk9z0x" /></button>
          <button type="button" className="btn btn-ghost btn-sm h-7 w-7 px-0" aria-label="আন্ডারলাইন" onMouseDown={e => {
          e.preventDefault();
          exec('underline');
        }} data-id="element-120" data-mp="34at65jndnjjx"><UnderlineIcon className="h-3.5 w-3.5" data-id="element-121" data-mp="2zm1ljep3hqth" /></button>
          <span className="mx-1 h-5 w-px bg-line" data-id="element-122" data-mp="2zjfctbqgx59o" />
          <button type="button" className="btn btn-ghost btn-sm h-7" onClick={addPara} data-id="element-123" data-mp="2lmsfh7ulsd38"><ListOrderedIcon className="h-3.5 w-3.5" data-id="element-124" data-mp="2pn2icbn3mndv" /> অনুচ্ছেদ যোগ</button>
          <button type="button" className="btn btn-ghost btn-sm h-7" onClick={onRegenerate} data-id="element-125" data-mp="2pp5k1sxm64yf"><RefreshCwIcon className="h-3.5 w-3.5" data-id="element-126" data-mp="3ddoqs410m9oe" /> PCR তথ্য থেকে পুনরায় তৈরি</button>
        </div>
      </div>
      <div key={file.bodyHtml} ref={bodyRef} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="নোটের মূল অংশ" className="mt-2 min-h-[260px] rounded-lg border border-line bg-[#FFFEFA] px-6 py-4 text-[15px] leading-8 text-ink focus:outline-none focus:ring-2 focus:ring-navy/25 [&_p]:mb-2 [&_p]:text-justify" dangerouslySetInnerHTML={{
      __html: file.bodyHtml
    }} data-id="element-127" data-mp="3ie41entwx32o" />
      <p className="mt-1 text-[11px] text-ink-muted" data-id="element-128" data-mp="1plpdz6zeh4cu">তফসিল-“ক” (অন্তর্ভুক্ত PCR তালিকা) PCR রেকর্ড থেকে স্বয়ংক্রিয়ভাবে নোটে যুক্ত হয় এবং সম্পাদনযোগ্য নয়।</p>

      <div className="mt-4 grid gap-4" data-id="element-129" data-mp="1zdle0nl1r4jp">
        <div data-id="element-130" data-mp="1zcjya5t35tvn">
          <label className="label" htmlFor="ne-obs" data-id="element-131" data-mp="1zf686c3g8lh1">কর্মকর্তার পর্যবেক্ষণ</label>
          <textarea id="ne-obs" rows={3} className="textarea" value={draft.observation} onChange={e => onDraft({
          ...draft,
          observation: e.target.value
        })} data-id="element-132" data-mp="1uf0l7984jn5a" />
        </div>
        <div data-id="element-133" data-mp="11qmkumtkceqk">
          <label className="label" htmlFor="ne-rec" data-id="element-134" data-mp="11r4i9mryoz02">সুপারিশ</label>
          <input id="ne-rec" className="input" value={draft.recommendation} onChange={e => onDraft({
          ...draft,
          recommendation: e.target.value
        })} data-id="element-135" data-mp="1gn3xrihei1f8" />
        </div>
      </div>
    </section>;
}